'use client';

import React, { 
  useState, 
  useEffect, 
  useRef, 
  useCallback, 
  useMemo,
  memo,
  ReactNode
} from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeSwitcher } from '../ui/adapters/ThemeSwitcher';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@repo/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ProductGroup } from '@/lib/data/products';
import { groupAndSortProductGroups } from '@/lib/utils';

// Types
interface NavigationItem {
  href: string;
  label: string;
  children?: NavigationItem[];
  groups?: ProductGroup[];
}

interface NavigationLinkProps {
  href: string;
  label: string;
  isActive: boolean;
  className?: string;
  onClick?: () => void;
  hasDropdown?: boolean;
  isDropdownOpen?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children?: ReactNode;
}

interface DropdownMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  id: string;
}

// Memoized Components
const NavigationLink = memo(function NavigationLink({ 
  href, 
  label, 
  isActive, 
  className = '', 
  onClick,
  hasDropdown = false,
  isDropdownOpen = false,
  onMouseEnter,
  onMouseLeave,
  children
}: NavigationLinkProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`text-sm font-medium px-2.5 py-1 rounded-md transition-colors duration-200 ${
        isActive
          ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
          : `text-gray-700 dark:text-gray-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`
      } ${className}`}
      aria-expanded={hasDropdown ? isDropdownOpen : undefined}
      aria-haspopup={hasDropdown ? 'menu' : undefined}
    >
      <span className="flex items-center">
        {label}
        {hasDropdown && (
          <ChevronDown 
            className={`ml-1 h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </span>
      {children}
    </Link>
  );
});

const DropdownMenu = memo(function DropdownMenu({ 
  isOpen, 
  onClose, 
  children, 
  className = '',
  id 
}: DropdownMenuProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}
          className={`absolute left-0 z-[9999] min-w-[220px] ${className}`}
          style={{ 
            top: "calc(100% + 10px)",
            filter: "drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))",
            minWidth: "220px",
            position: "absolute"
          }}
          id={id}
          role="menu"
          aria-orientation="vertical"
        >
          <div className="premium-radius-lg overflow-hidden premium-glass-nav dark:premium-glass-dark-nav bg-white/95 dark:bg-gray-900/98 backdrop-blur-sm">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});



// Main Navigation Component
const Navigation = ({ 
  headerOpacity = 1,
  productGroups = [],
}: {
  headerOpacity?: number;
  productGroups?: ProductGroup[];
}): React.ReactElement => {
  // State management
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const router = useRouter();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [mobileOpenSubMenu, setMobileOpenSubMenu] = useState<string | null>(null);
  const [mobileOpenGroupSlug, setMobileOpenGroupSlug] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeGroupSlug, setActiveGroupSlug] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  

  // Memoize the navigation items to prevent unnecessary recalculations
  const navigationItems = useMemo(() => {
    const publicItems: NavigationItem[] = [
      {
        href: '/products',
        label: 'Products',
        groups: productGroups.length > 0 ? groupAndSortProductGroups(productGroups) : undefined,
      },
      { href: '/industries', label: 'Industries We Serve' },
      { href: '/about', label: 'About' },
      { href: '/contact', label: 'Contact Us' },
    ];

    return user ? [...publicItems] : publicItems;
  }, [productGroups, user]);

  // Define navigation item subsets for different contexts
  const mainNavItems = navigationItems.filter(item => item.href !== '/dashboard');
  const mobileItems = navigationItems;

  // Memoize the isRouteActive function
  const isRouteActive = useCallback((item: NavigationItem): boolean => {
    if (!pathname) return false;
    
    if (pathname === item.href) return true;
    
    if (item.children) {
      return item.children.some(child => 
        pathname === child.href || pathname.startsWith(child.href)
      );
    }
    
    return false;
  }, [pathname]);

  // Memoize the handleMouseEnter function
  const handleMouseEnter = useCallback((href: string) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    setOpenDropdown(href);
    if (href === '/products' && productGroups.length > 0) {
      setActiveGroupSlug(productGroups[0]?.slug ?? null);
    }
  }, [productGroups]);

  // Memoize the handleMouseLeave function
  const handleMouseLeave = useCallback(() => {
    leaveTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
      setActiveGroupSlug(null);
    }, 200);
  }, []);

  // Memoize the toggleMobileSubMenu function
  const toggleMobileSubMenu = useCallback((href: string) => {
    setMobileOpenSubMenu(prev => prev === href ? null : href);
  }, []);

  // Memoize the handleSignOut function
  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      router.push('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [router, signOut]);

  // Effect for scroll handling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Effect for outside click handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const mobileMenuContainer = document.querySelector('.mobile-menu-container');
      const mobileMenuButton = document.querySelector('.mobile-menu-button');
      
      if (
        mobileMenuOpen &&
        mobileMenuContainer && 
        !mobileMenuContainer.contains(event.target as Node) &&
        mobileMenuButton &&
        !mobileMenuButton.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  // Effect for dropdown outside click handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Effect to close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenSubMenu(null);
  }, [pathname]);

  // Set hasMounted after initial render
  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)]">
      <div 
        className={`p-2 sm:p-3 transition-[padding,opacity] duration-300 overflow-visible`}
        style={{ opacity: headerOpacity }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 overflow-visible">
          <div className={`premium-radius-xl px-3 sm:px-4 py-1 flex items-center justify-between transition-all duration-300 overflow-visible ${
            hasMounted && scrolled 
              ? 'premium-glass-nav dark:premium-glass-dark-nav premium-depth-lg dark:premium-depth-xl bg-white/70 backdrop-blur-sm py-1 sm:py-1.5' 
              : 'bg-transparent py-1.5 sm:py-2'
          }`}>
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link 
                href="/" 
                className="flex items-center"
                aria-label="PAVISHA Home"
              >
                <span className="premium-gradient-text text-sm sm:text-base md:text-lg font-bold">PAVISHA</span>
              </Link>
            </div>

            {/* Desktop Navigation - Center aligned with enhanced premium glass effect */}
            <nav 
              className="hidden lg:flex items-center gap-2 sm:gap-6 relative z-20 overflow-visible" 
              ref={navRef}
              role="navigation"
              aria-label="Main Navigation"
            >
              {mainNavItems.map((item) => (
                <div key={item.href} className="relative overflow-visible">
                  {(item.children || item.groups) ? (
                    <div 
                      className="relative overflow-visible"
                      onMouseEnter={() => handleMouseEnter(item.href)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        className={`text-sm font-medium px-2.5 py-1 rounded-md transition-colors duration-200 flex items-center ${
                          isRouteActive(item)
                            ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
                            : `${hasMounted && scrolled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-700 dark:text-gray-200'} hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`
                        }`}
                        aria-expanded={openDropdown === item.href}
                        aria-controls={`dropdown-${item.href.replace('/', '-')}`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown 
                          className={`ml-1 h-4 w-4 transition-transform duration-200 ${openDropdown === item.href ? 'transform rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      
                      <AnimatePresence>
                        {openDropdown === item.href && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}

                            onMouseEnter={() => handleMouseEnter(item.href)}
                            className="absolute left-0 z-[9999] min-w-[220px]"
                            style={{ 
                              top: "calc(100% + 10px)",
                              filter: "drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))",
                              minWidth: "220px",
                              position: "absolute"
                            }}
                            id={`dropdown-${item.href.replace('/', '-')}`}
                            role="menu"
                            aria-orientation="vertical"
                          >
                            <div
                              className="premium-radius-lg overflow-hidden premium-glass-nav dark:premium-glass-dark-nav bg-white/95 dark:bg-gray-900/98 backdrop-blur-sm"
                            >
                              <div className="relative" onMouseLeave={() => setActiveSubMenu(null)}>
                                <div className="py-1 w-full">
                                  {item.groups?.map((group) => {
                                    // A group with a fly-out menu for its categories
                                    if (group.slug === 'packaging-accessories') {
                                      return (
                                        <div 
                                          key={group.slug} 
                                          className="relative"
                                        >
                                          <button
                                            onMouseEnter={() => setActiveSubMenu(group.slug)}
                                            className={`flex justify-between items-center w-full px-4 py-2 text-sm text-left transition-colors duration-200 ${activeSubMenu === group.slug ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 'text-gray-700 dark:text-gray-200'} hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`}
                                          >
                                            <span>{group.name}</span>
                                            <ChevronRight className="h-4 w-4" />
                                          </button>

                                          {activeSubMenu === group.slug && (
                                            <div className="absolute left-full top-0 mt-0 w-max premium-radius-lg shadow-lg premium-glass-nav dark:premium-glass-dark-nav bg-white/95 dark:bg-gray-900/98 backdrop-blur-sm">
                                              <div className="py-1">
                                                {group.categories.map((category) => (
                                                  <Link
                                                    key={category.id}
                                                    href={`/products/${category.slug}`}
                                                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400"
                                                    onClick={() => setOpenDropdown(null)}
                                                    role="menuitem"
                                                  >
                                                    {category.name}
                                                  </Link>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }

                                    // Direct links for categories in other groups
                                    return group.categories.map(category => (
                                      <Link
                                        key={category.id}
                                        href={`/products/${category.slug}`}
                                        className={`block px-4 py-2 text-sm transition-colors duration-200 text-gray-700 dark:text-gray-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`}
                                        onClick={() => setOpenDropdown(null)}
                                        role="menuitem"
                                      >
                                        <span>{category.name}</span>
                                      </Link>
                                    ));
                                  })}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className={`text-sm font-medium px-2.5 py-1 rounded-md transition-colors duration-200 ${
                        pathname === item.href
                          ? "bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400"
                          : `${hasMounted && scrolled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-700 dark:text-gray-200'} hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10 dark:hover:text-red-400`
                      }`}
                    >
                      <span>{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {!user ? (
                <>
                  {/* Only show login on larger screens */}
                  <div className="hidden lg:block">
                    <Link
                      href="/auth/login"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 premium-transition-all"
                    >
                      Login
                    </Link>
                  </div>
                  
                  {/* Only show on appropriate sized screens */}
                  <div className="hidden lg:block">
                    <Link
                      href="/contact"
                      className="text-xs sm:text-sm font-medium premium-gradient-primary text-white px-2.5 sm:px-4 py-1 sm:py-1.5 premium-radius-md premium-shadow premium-transition-all premium-hover-lift"
                    >
                      Get a Quote
                    </Link>
                  </div>
                </>
              ) : (
                <button
                  onClick={handleSignOut}
                  className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                >
                  Sign out
                </button>
              )}
            </div>

            {/* Mobile Menu Button with premium effect */}
            <div className="flex lg:hidden ml-2 sm:ml-4 items-center h-full">
              <button
                type="button"
                aria-label="Toggle menu"
                className="mobile-menu-button inline-flex items-center justify-center p-1.5 sm:p-2 premium-radius-md text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all premium-hover-lift focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                style={{ 
                  willChange: "transform",
                  transform: "translateZ(0)"
                }}
              >
                <span className="sr-only">Toggle menu</span>
                {!mobileMenuOpen ? (
                  <svg className="block h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown with premium glass effect matching navbar */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <div className="mobile-menu-container relative z-50">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  type: "tween", 
                  ease: "easeOut", 
                  duration: 0.25 
                }}
                className="absolute top-2 right-0 left-0 mx-4 sm:mx-auto sm:max-w-md p-4 premium-radius-xl premium-glass-nav dark:premium-glass-dark-nav premium-depth-lg dark:premium-depth-xl backdrop-blur-sm overflow-hidden"
                style={{ 
                  transform: "translate3d(0, 0, 0)",
                  willChange: "transform, opacity",
                  backfaceVisibility: "hidden"
                }}
              >
                <div className="pb-2 relative z-10">
                  {/* Navigation Items with premium glass effect */}
                  {mobileItems.map((item) => (
                    <div key={item.href}>
                      {(item.children || item.groups) ? (
                        <div className="mb-1">
                          <button
                            onClick={() => toggleMobileSubMenu(item.href)}
                            className={`flex justify-between items-center w-full py-2.5 text-sm font-medium premium-radius-md ${
                              isRouteActive(item) ? 'mobile-nav-link-active' : 'mobile-nav-link'
                            }`}
                            aria-expanded={mobileOpenSubMenu === item.href}
                            aria-controls={`mobile-submenu-${item.href.replace('/', '-')}`}
                          >
                            <span>{item.label}</span>
                            <ChevronDown 
                              className={`h-4 w-4 transition-transform duration-200 ${mobileOpenSubMenu === item.href ? 'transform rotate-180' : ''}`}
                              aria-hidden="true"
                            />
                          </button>
                          
                          <AnimatePresence>
                            {mobileOpenSubMenu === item.href && (
                              <motion.div
                                initial={{ height: 0, opacity: 0.5 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0.5 }}
                                transition={{ type: "tween", ease: "easeOut", duration: 0.25 }}
                                className="pl-2 ml-3 border-l-2 border-gray-200 dark:border-gray-700 overflow-hidden"
                                id={`mobile-submenu-${item.href.replace('/', '-')}`}
                                role="menu"
                              >
                                {item.groups ? (
                                  item.groups.map((group) => {
                                    // For 'packaging-accessories', create a collapsible sub-menu
                                    if (group.slug === 'packaging-accessories') {
                                      return (
                                        <div key={group.slug}>
                                          <button
                                            onClick={() => setMobileOpenGroupSlug(mobileOpenGroupSlug === group.slug ? null : group.slug)}
                                            className="flex justify-between items-center w-full py-2 pl-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
                                          >
                                            <span>{group.name}</span>
                                            <ChevronDown
                                              className={`h-4 w-4 transition-transform ${mobileOpenGroupSlug === group.slug ? 'rotate-180' : ''}`}
                                            />
                                          </button>
                                          <AnimatePresence>
                                            {mobileOpenGroupSlug === group.slug && (
                                              <motion.div
                                                initial={{ height: 0, opacity: 0.5 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0.5 }}
                                                transition={{ type: "tween", ease: "easeOut", duration: 0.25 }}
                                                className="pl-2 ml-3 border-l-2 border-gray-200 dark:border-gray-700 overflow-hidden"
                                              >
                                                {group.categories.map((category) => (
                                                  <Link
                                                    key={category.id}
                                                    href={`/products/${category.slug}`}
                                                    className={`flex items-center py-2 pl-3 text-sm ${
                                                      pathname === `/products/${category.slug}`
                                                        ? 'text-red-600 dark:text-red-400 font-medium'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
                                                    }`}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                  >
                                                    {category.name}
                                                  </Link>
                                                ))}
                                              </motion.div>
                                            )}
                                          </AnimatePresence>
                                        </div>
                                      );
                                    }

                                    // For other groups, list categories directly
                                    return group.categories.map(category => (
                                      <Link
                                        key={category.id}
                                        href={`/products/${category.slug}`}
                                        className={`flex items-center py-2 pl-3 text-sm ${
                                          pathname === `/products/${category.slug}`
                                            ? 'text-red-600 dark:text-red-400 font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                                        }`}
                                        onClick={() => setMobileMenuOpen(false)}
                                        role="menuitem"
                                      >
                                        <span>{category.name}</span>
                                      </Link>
                                    ));
                                  })
                                ) : (
                                  item.children?.map((child) => (
                                    <Link
                                      key={child.href}
                                      href={child.href}
                                      className={`flex items-center py-2 pl-3 text-sm ${
                                        pathname === child.href
                                          ? 'text-red-600 dark:text-red-400 font-medium'
                                          : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                                      }`}
                                      onClick={() => setMobileMenuOpen(false)}
                                      role="menuitem"
                                    >
                                      <span>{child.label}</span>
                                    </Link>
                                  ))
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          href={item.href}
                          className={`block py-2.5 text-sm font-medium premium-radius-md ${
                            pathname === item.href ? 'mobile-nav-link-active' : 'mobile-nav-link'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span>{item.label}</span>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Auth Actions with premium styling */}
                <div className="pt-3 pb-1 border-t border-gray-100 dark:border-gray-800 relative z-10">
                  {!user ? (
                    <div className="flex flex-col space-y-2">
                      <Link
                        href="/auth/login"
                        className="block py-2.5 text-sm font-medium premium-radius-md mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Log in to your account"
                      >
                        Login
                      </Link>
                      <Link
                        href="/contact"
                        className="block py-2.5 text-sm font-medium premium-gradient-primary text-white premium-radius-md premium-shadow text-center"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Get a quote for our products"
                      >
                        Get a Quote
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        handleSignOut();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 premium-radius-md"
                    >
                      Sign out
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default memo(Navigation);